import type { AxiosError } from 'axios'

export interface FieldError {
  field: string
  message: string
}

export interface ApiError {
  status: number
  code: string
  message: string
  fieldErrors?: FieldError[]
  /**
   * Solo presente en respuestas de error que el backend marca con un email
   * relevante para el caller. Hoy: {@code error.email.not.verified} —
   * login bloqueado por email sin verificar, el BE devuelve el email del
   * user para que el FE pueda ofrecer "Reenviar enlace" sin pedirlo otra
   * vez.
   */
  email?: string
}

interface BackendErrorBody {
  status: number
  code?: string
  message?: string
  fieldErrors?: FieldError[]
  email?: string
}

export function normalizeApiError(error: unknown): ApiError {
  // Idempotente: el interceptor de `httpClient` ya rechaza con un `ApiError`
  // normalizado, así que en los `onError`/`catch` el valor que llega YA lo es.
  // Volver a normalizarlo perdería el `code` (un ApiError no tiene `.response`)
  // y caería a `error.unknown`. Detectamos la forma ya-normalizada y la
  // devolvemos intacta. Origen: el mensaje de "contraseña incorrecta" salía
  // como genérico (baja de cuenta + cambio de contraseña), sep-2026.
  if (
    error !== null &&
    typeof error === 'object' &&
    typeof (error as ApiError).code === 'string' &&
    typeof (error as ApiError).status === 'number' &&
    !('isAxiosError' in error) &&
    !('response' in error)
  ) {
    return error as ApiError
  }

  const axiosErr = error as AxiosError<BackendErrorBody>
  const body = axiosErr.response?.data
  return {
    status: axiosErr.response?.status ?? 0,
    code: body?.code ?? 'error.unknown',
    message: body?.message ?? axiosErr.message ?? 'Unknown error',
    fieldErrors: body?.fieldErrors,
    email: body?.email,
  }
}

/**
 * Compone un mensaje legible a partir de un `ApiError`, dando prioridad a
 * los `fieldErrors` (más informativos para Bean Validation) sobre el
 * `message` genérico tipo "Error de validación en los datos enviados".
 *
 * <p>Ejemplos:</p>
 * <ul>
 *   <li>`{ message: "Partida llena" }` → `"Partida llena"`.</li>
 *   <li>`{ message: "Error de validación...", fieldErrors: [{field:"maxPlayers", message:"debe ser ≤ 20"}] }`
 *       → `"maxPlayers: debe ser ≤ 20"`.</li>
 *   <li>Varios fieldErrors → separados por ` · `.</li>
 * </ul>
 *
 * <p>Para mostrar en banners "global" del formulario. Si el caller quiere
 * pintar el error pegado a su campo, debe leer `fieldErrors` directamente
 * en lugar de usar este helper.</p>
 */
export function formatApiErrorMessage(error: ApiError | undefined | null): string | null {
  if (!error) return null
  if (error.fieldErrors && error.fieldErrors.length > 0) {
    return error.fieldErrors.map((fe) => `${fe.field}: ${fe.message}`).join(' · ')
  }
  return error.message
}
